import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Phone, ArrowRight, ArrowUpRight, Star, ShieldCheck, Smile, Tag, Truck,
  BadgeCheck, UserCheck, Home as HomeIcon, Car, Sparkles,
} from "lucide-react";
import AnimatedText from "../components/site/AnimatedText";
import Reveal from "../components/site/Reveal";
import EditorialMarquee from "../components/site/EditorialMarquee";
import { SectionLabel } from "../components/site/Bits";
import {
  BUSINESS, IMAGES, WHY_CHOOSE, DOMESTIC_SERVICES, VALET_PACKAGES,
  REVIEWS, AREAS,
} from "../data/content";

const ICONS = { ShieldCheck, Smile, Tag, Truck, BadgeCheck, UserCheck };

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <div data-testid="home-page">
      {/* ---------------- HERO ---------------- */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-end overflow-hidden bg-navy grain">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
          <img src={IMAGES.carPolish} alt="Professional car valeting" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/80 to-navy/40" />
        </motion.div>

        <div className="relative w-full mx-auto max-w-[1400px] px-5 sm:px-8 pb-16 lg:pb-24 pt-28">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="mb-7"
          >
            <SectionLabel dark>Preston &amp; Lancashire</SectionLabel>
          </motion.div>

          <h1 className="font-heading font-extrabold text-white text-[2.6rem] sm:text-6xl lg:text-[5.4rem] leading-[0.92] tracking-tighter max-w-5xl">
            <AnimatedText
              lines={["Professional Domestic Cleaning", "& Mobile Car Valeting"]}
              lineClassName=""
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            className="mt-7 text-lg text-white/75 max-w-xl leading-relaxed"
          >
            Reliable, affordable and professional cleaning services for your home and vehicle.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/book"
              data-testid="hero-book-now"
              className="group inline-flex items-center gap-2 rounded-full bg-brandgreen px-7 py-4 text-white font-semibold hover:bg-white hover:text-navy transition-colors duration-300"
            >
              Book Now
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-get-quote"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-4 text-white font-semibold hover:bg-white/10 transition-colors duration-300"
            >
              Get a Free Quote
            </Link>
          </motion.div>
        </div>
      </section>

      <EditorialMarquee />

      {/* ---------------- SERVICES SHOWCASE ---------------- */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="max-w-3xl mb-16">
            <Reveal><SectionLabel>Our Services</SectionLabel></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-heading font-extrabold text-navy text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[0.98]">
                Two specialities. One standard of clean.
              </h2>
            </Reveal>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Domestic */}
            <Reveal>
              <ServiceCard
                image={IMAGES.cleanHome}
                icon={HomeIcon}
                kicker="For your home"
                title="Domestic Cleaning"
                items={DOMESTIC_SERVICES.slice(0, 5)}
                to="/domestic-cleaning"
                cta="Learn More"
              />
            </Reveal>
            {/* Valeting */}
            <Reveal delay={0.12}>
              <ServiceCard
                image={IMAGES.carWash}
                icon={Car}
                kicker="For your vehicle"
                title="Mobile Car Valeting"
                items={["Mini Valet", "Full Valet", "Interior Valet", "Exterior Wash", "Deep Valet"]}
                to="/mobile-car-valeting"
                cta="View Packages"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------------- WHY CHOOSE US (BENTO) ---------------- */}
      <section className="py-24 lg:py-32 bg-mist">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <Reveal><SectionLabel>Why Choose Us</SectionLabel></Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-5 font-heading font-extrabold text-navy text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[0.98]">
                  Trusted, insured and genuinely reliable.
                </h2>
              </Reveal>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_CHOOSE.map((item, i) => {
              const Icon = ICONS[item.icon] || ShieldCheck;
              return (
                <Reveal key={item.title} delay={i * 0.06}>
                  <div
                    data-testid={`why-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group h-full rounded-2xl bg-white border border-black/5 p-8 hover:border-brandgreen/40 hover:-translate-y-1 transition-[transform,border-color] duration-300"
                  >
                    <span className="grid place-items-center w-12 h-12 rounded-xl bg-navy text-white group-hover:bg-brandgreen transition-colors duration-300">
                      <Icon className="w-6 h-6" strokeWidth={2} />
                    </span>
                    <h3 className="mt-6 font-heading font-bold text-navy text-xl tracking-tight">{item.title}</h3>
                    <p className="mt-2 text-muted-foreground text-sm leading-relaxed">{item.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------- REVIEWS ---------------- */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="max-w-2xl mb-14">
            <Reveal><SectionLabel>Customer Reviews</SectionLabel></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-heading font-extrabold text-navy text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
                Loved by homes across Lancashire.
              </h2>
            </Reveal>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            {REVIEWS.map((r, i) => (
              <Reveal key={i} delay={(i % 2) * 0.1}>
                <figure className="h-full rounded-2xl border border-black/5 bg-mist p-8 flex flex-col">
                  <div className="flex gap-1 text-brandgreen mb-5">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} className="w-4 h-4 fill-brandgreen" />
                    ))}
                  </div>
                  <blockquote className="font-heading text-xl sm:text-2xl text-navy tracking-tight leading-snug flex-1">
                    &ldquo;{r.text}&rdquo;
                  </blockquote>
                  <figcaption className="mt-6 text-sm text-muted-foreground">
                    — {r.author}, {r.place}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 text-xs text-muted-foreground italic">
            Sample reviews — to be replaced with genuine customer feedback.
          </p>
        </div>
      </section>

      {/* ---------------- SPECIAL OFFER ---------------- */}
      <section className="py-8 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-brandgreen text-white px-8 py-12 lg:px-16 lg:py-16 grain">
              <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
                <div>
                  <SectionLabel className="!text-white">New Customer Offer</SectionLabel>
                  <h2 className="mt-4 font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none">
                    10% OFF
                  </h2>
                  <p className="mt-3 text-white/85 text-lg">Your first booking with Mark Clean Services.</p>
                </div>
                <Link
                  to="/book"
                  data-testid="offer-book-today"
                  className="group inline-flex items-center gap-2 rounded-full bg-white text-navy px-8 py-4 font-semibold hover:bg-navy hover:text-white transition-colors duration-300 shrink-0"
                >
                  Book Today
                  <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- AREAS COVERED ---------------- */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal><SectionLabel>Areas We Cover</SectionLabel></Reveal>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-2">
            {AREAS.map((a, i) => (
              <Reveal key={a} delay={i * 0.04}>
                <span className="font-heading font-bold text-navy text-3xl sm:text-4xl lg:text-5xl tracking-tighter hover:text-brandgreen transition-colors cursor-default">
                  {a}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- CTA ---------------- */}
      <section className="relative py-24 lg:py-36 bg-navy text-white overflow-hidden grain">
        <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 text-center">
          <Reveal>
            <Sparkles className="w-8 h-8 text-brandgreen mx-auto mb-8" />
            <h2 className="font-heading font-extrabold text-4xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              Ready to book?
            </h2>
            <a
              href={BUSINESS.phoneHref}
              className="mt-8 inline-flex items-center gap-3 font-heading text-3xl sm:text-4xl text-brandgreen hover:text-white transition-colors"
              data-testid="cta-phone"
            >
              <Phone className="w-7 h-7" /> {BUSINESS.phone}
            </a>
            <div className="mt-10">
              <Link
                to="/book"
                className="inline-flex items-center gap-2 rounded-full bg-brandgreen px-8 py-4 font-semibold hover:bg-white hover:text-navy transition-colors duration-300"
                data-testid="cta-book-online"
              >
                Book Online Today <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function ServiceCard({ image, icon: Icon, kicker, title, items, to, cta }) {
  return (
    <Link
      to={to}
      data-testid={`service-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
      className="group relative block overflow-hidden rounded-3xl bg-navy h-full min-h-[520px]"
    >
      <div className="absolute inset-0">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover opacity-45 group-hover:opacity-55 group-hover:scale-105 transition-[opacity,transform] duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/20" />
      </div>
      <div className="relative h-full flex flex-col justify-between p-8 lg:p-10 text-white">
        <div className="flex items-center justify-between">
          <span className="grid place-items-center w-12 h-12 rounded-xl bg-white/10 backdrop-blur">
            <Icon className="w-6 h-6" />
          </span>
          <span className="grid place-items-center w-11 h-11 rounded-full bg-brandgreen group-hover:rotate-45 transition-transform duration-300">
            <ArrowUpRight className="w-5 h-5" />
          </span>
        </div>
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-brandgreen font-semibold">{kicker}</span>
          <h3 className="mt-2 font-heading font-extrabold text-3xl lg:text-4xl tracking-tight">{title}</h3>
          <ul className="mt-6 flex flex-wrap gap-2">
            {items.map((it) => (
              <li key={it} className="text-sm rounded-full border border-white/20 px-3 py-1.5 text-white/85">
                {it}
              </li>
            ))}
          </ul>
          <span className="mt-7 inline-flex items-center gap-2 font-semibold text-white group-hover:text-brandgreen transition-colors">
            {cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </div>
      </div>
    </Link>
  );
}
